import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Divider as MuiDivider,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  ListItemIcon,
  ListItemText,
  CardContent,
  Alert,
  Tooltip,
  linearProgressClasses,
  Avatar,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IContract } from "../../../../types/contract";
import { Check, Error, Gavel, Info, Warning } from "@mui/icons-material";
import OwlAvatarIcon from "../../../../icons/OwlAvatarIcon";
import { useGetTermsAndConditionsDocumentQuery } from "../../../../redux/slices/indexApiSlice";
import { ContractExtractionResult } from "../../../../types/aiOutput";
import { LinearProgressWithLabel } from "../../dashboards/Analytics/PromptsSideBar";
import { red } from "@mui/material/colors";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import RelevantSectionsSpykio from "./RelevantSectionsSpykio";
import LogicalCheckAlert from "./LogicalCheckAlert";

const Divider = styled(MuiDivider)(spacing);

// create an enum for the tabs
export enum SideBarTabIndex {
  BASIC_DETAILS,
  // SCORES,
  SCORES_V2,
  FIELDS_FOUND,
  FIELDS_NOT_FOUND,
  FIELDS_EXCLUDED,
  FIELDS_SUGGESTED,
  CONTRACT_MANAGEMENT,
  AI_ASSISTANT,
}

export const ScoreBar = styled(LinearProgressWithLabel)(({ theme }) => ({
  height: 14,
  borderRadius: 7,

  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "transparent",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 7,
    backgroundColor: red[600],
  },
}));

const TCDeviationsSection = ({
  contract,
  aiOutputs,
}: {
  contract: IContract;
  aiOutputs: ContractExtractionResult[];
}) => {
  const { t } = useTranslation();

  const [max, setMax] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [riskLabel, setRiskLabel] = useState<string>("");

  const [latestOutput, setLatestOutput] = useState<ContractExtractionResult>();

  const { data: termsDocument } = useGetTermsAndConditionsDocumentQuery(
    latestOutput?.termsAndConditions?.documentId ?? skipToken
  );

  useEffect(() => {
    const latestOutput = aiOutputs.filter(
      (a) => a.termsAndConditions && a.termsAndConditions.scores?.length > 0
    );

    if (latestOutput.length > 0) {
      setLatestOutput(latestOutput[0]);
    }
  }, [aiOutputs]);

  useEffect(() => {
    if (contract?.deviations) {
      const newMax = contract.deviations?.length * 10;
      setMax(newMax);

      const newTotal = contract.deviations?.reduce(
        (acc, curr) => acc + (curr.details?.score ?? 10),
        0
      );
      setTotal(newTotal);

      const perc = (newTotal / newMax) * 100;
      if (perc > 90) {
        setRiskLabel("Very high risk");
      } else if (perc > 70) {
        setRiskLabel("High risk");
      } else if (perc > 30) {
        setRiskLabel("Medium risk");
      } else if (perc > 10) {
        setRiskLabel("Low risk");
      } else {
        setRiskLabel("Very low risk");
      }
    }
  }, [contract]);

  return (
    <Box display="flex" flexDirection="column" gap={5}>
      {/* <Typography variant="h6" sx={{ mb: 2 }}>
              {t("Fields found")}
            </Typography> */}

      {contract?.deviations && contract?.deviations?.length > 0 && (
        <Card
          // backgroundColor={backgroundColor}
          sx={{ width: "100%", display: "flex" }}
        >
          <CardContent
            sx={{
              color: "inherit",
              width: "100%",
            }}
          >
            {riskLabel === "Very high risk" ? (
              <Error />
            ) : riskLabel === "High risk" ? (
              <Warning />
            ) : riskLabel === "Medium risk" ? (
              <Info />
            ) : (
              <Check />
            )}
            <Typography variant="h4" mt={2} mb={1}>
              {t(riskLabel)}
            </Typography>

            {termsDocument && (
              <Typography variant="body1">
                <Link
                  to={`/libraries/terms-and-conditions/${termsDocument.id}`}
                >
                  {termsDocument.name}
                </Link>
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Box>
        {contract?.deviations?.map((deviation) => (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ListItem disablePadding disableGutters>
                <ListItemIcon>
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 32,
                      height: 32,
                      color: (theme) => theme.palette.common.white,
                      bgcolor: (theme) => theme.palette.grey[900],
                      fontSize: 12,
                    }}
                  >
                    {deviation?.details?.article?.articleNumber || <Gavel />}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Grid
                      container
                      direction="row"
                      gap={5}
                      size={12}
                      flexWrap="nowrap"
                      alignItems="center"
                    >
                      <Grid size={4}>
                        <Typography variant="body1" textAlign="left">
                          {deviation.details?.article?.title}
                        </Typography>

                        {/* <Typography variant="caption">{score.explanation}</Typography> */}
                      </Grid>

                      <Grid size={8}>
                        <Tooltip
                          title={
                            <Typography>
                              {deviation?.details?.explanation}
                            </Typography>
                          }
                        >
                          <div>
                            <ScoreBar
                              variant="determinate"
                              value={
                                deviation?.details?.score === undefined
                                  ? 10
                                  : (deviation.details.score / 10) * 100
                              }
                              hideLabel
                            />
                          </div>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  }
                />
              </ListItem>
            </AccordionSummary>
            <AccordionDetails>
              <Alert
                sx={{ mt: 2 }}
                variant="outlined"
                icon={<OwlAvatarIcon />}
                severity="info"
              >
                <Typography variant="body1">
                  {deviation.details?.explanation}
                </Typography>

                {deviation.context && (
                  <RelevantSectionsSpykio spykioContext={deviation.context} />
                )}

                {deviation.logicalCheck && (
                  <LogicalCheckAlert logicalCheck={deviation.logicalCheck} />
                )}
              </Alert>

              <Box pt={2} display="flex" justifySelf="flex-end">
                <Tooltip title={deviation.details?.article?.body}>
                  <Info />
                </Tooltip>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default TCDeviationsSection;
